<template>
  <div>
    <Treeselect
      v-if="product.variants && product.variants.length > 0"
      size="sm"
      class="mb-3"
      :options="productVariants"
      v-model="variant_id"
      :clearable="false"
      :flat="true"
      :default-expand-level="1"
      :noResultsText="trans('no_result')"
    />
    <b-form v-if="!invoice_item && allowSettingAmount" inline class="justify-content-center" @submit.prevent="onSetAmount">
      <b-button v-if="!show_set_amount" variant="link" size="sm" class="text-branded mb-2" @click="show_set_amount = true" :disabled="busy || !!priceError">
        <span v-if="!show_set_amount">
          <i class="fa fa-fw fa-edit"></i>
          {{ trans('change_card_amount') }}
        </span>
        <span v-if="show_set_amount">
          {{ trans('hide') }}
        </span>
      </b-button>
      <b-form-group v-if="show_set_amount" class="mb-2">
        <b-form-input
          type="number"
          v-model="price"
          size="sm"
          :min="product.gift_card_rules.mininum || 5"
          :maximum="product.gift_card_rules.maximum || 1000"
          :state="!!priceError ? false : null"
          :disabled="busy"
          style="width: 110px;"
        />
        <b-form-invalid-feedback :state="!!priceError ? false : null">
          {{ priceError }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group v-if="show_set_amount" class="ml-2 mb-2">
        <b-button variant="branded" size="sm" @click="onSetAmount" :disabled="busy || !!priceError">
          <i v-if="!busy" class="fa fa-fw fa-check"></i>
          <i v-if="busy" class="fa fa-fw fa-circle-notch fa-spin"></i>
        </b-button>
      </b-form-group>
    </b-form>
    <b-form v-if="!invoice_item || variant_id" inline class="justify-content-center" @submit.prevent="onAddToCart">
      <b-form-group class="mb-2" v-if="productType != 'membership'">
        <b-form-spinbutton
          v-model="quantity"
          size="sm"
          inline
          :min="1"
          :max="product.quantity || 100"
          :disabled="busy || show_set_amount || outOfStock"
        />
      </b-form-group>
      <b-form-group class="mb-2 ml-2">
        <b-button variant="branded" size="sm" @click="onAddToCart" :disabled="busy || show_set_amount || outOfStock">
          <i v-if="!busy" class="fa fa-fw fa-plus"></i>
          <i v-if="busy" class="fa fa-fw fa-circle-notch fa-spin"></i>
        </b-button>
      </b-form-group>
      <b-form-invalid-feedback :state="!!error ? false : null">
        {{ error }}
      </b-form-invalid-feedback>
    </b-form>
    <p v-if="outOfStock" class="text-danger text-center">
      {{ trans('out_of_stock') }}
    </p>

    <div v-if="!!invoice_item && !variant_id" class="d-inline-block text-left">
      <b-button variant="success" size="sm" disabled>
        <i v-if="!busy" class="fa fa-fw fa-check"></i>
        <i v-if="busy" class="fa fa-fw fa-circle-notch fa-spin"></i>
      </b-button>
      <span class="ml-2">
        x{{ invoice_item.quantity }}
        =<dollar :value="invoice_item.amount" class="ml-1 lead font-weight-bold" />
      </span>
      <b-button variant="link" size="sm" class="ml-2 text-danger" :disabled="busy" @click="onRemoveFromCart">
        <i class="fa fa-fw fa-times"></i>
      </b-button>

      <div v-if="canApplyCoupon">
        <div v-if="!show_apply_coupon" class="my-2">
          <b-button variant="outline-info" size="sm" :disabled="busy || !!invoice_item.coupon || !!invoice_item.promo_code" @click="show_apply_coupon = true">
            <i class="fa fa-fw fa-ticket-alt"></i>
          </b-button>
          <span class="ml-2">
            <span v-if="!!invoice_item.coupon" class="pl-0">
              {{ trans('coupon') }} {{ invoice_item.coupon.code }}
              <dollar :value="-invoice_item.discount" class=" ml-1 lead font-weight-bold" />
            </span>
            <span v-if="!!invoice_item.promo_code" class="pl-0">
              {{ trans('promo_code') }} {{ invoice_item.promo_code.code }}
              <dollar :value="-invoice_item.discount" class=" ml-1 lead font-weight-bold" />
            </span>
            <span v-if="!invoice_item.coupon && !invoice_item.promo_code">
              {{ trans('add_a_coupon') }}
            </span>
            <b-button v-if="!!invoice_item.coupon || !!invoice_item.promo_code" variant="link" size="sm" class="text-danger" :disabled="busy" @click="removeCoupon(invoice_item)">
              <i class="fa fa-fw fa-times"></i>
            </b-button>
          </span>
        </div>
        <apply-coupon
          v-if="show_apply_coupon"
          :organization="organization"
          type="product"
          :invoice="invoice"
          :invoice_item="invoice_item"
          class="my-2"
          @close="onCloseApplyCoupon"
          @invoice="onCouponApplied"
        ></apply-coupon>
      </div>
    </div>

    <product-admin-options
      v-if="!!invoice_item && product.admin_can_change_price && adminCanModifyPrice"
      :organization="organization"
      :invoice_item="invoice_item"
      class="my-2"
      @invoice="onAdminOptionsApplied"
    ></product-admin-options>
  </div>
</template>

<style>
  .vue-treeselect__label {
    color: #45526e;
    cursor: pointer;
  }
  .vue-treeselect>.vue-treeselect__control {
    height: 32px;
  }
</style>

<script>
  import Dollar from './../billing/Dollar'
  import ApplyCoupon from './PackageApplyCoupon'
  import ProductAdminOptions from './ProductAdminOptions'
  import Treeselect from '@riophae/vue-treeselect'

  export default {
    props: {
      organization: Object,
      product: Object,
      adminCanModifyPrice: Boolean,
      productType: {
        type: String,
        default: 'product'
      }
    },
    components: {
      Dollar,
      ApplyCoupon,
      ProductAdminOptions,
      Treeselect
    },
    data() {
      let price = this.product.price
      if (this.product.variants && this.product.variants.length > 0) {
        price = this.product.variants[0].price
        this.product.quantity = this.product.variants[0].quantity
        this.setTopAmount(price)

        if (this.product.variants[0].image_url) {
          this.setImage(this.product.variants[0].image_url)
        }
      }

      return {
        trans: trans,
        numeral: numeral,
        busy: false,
        error: null,
        price: price,
        quantity: 1,
        code: null,
        invoice: null,
        show_apply_coupon: false,
        show_set_amount: false,
        variant_id: this.product.variants && this.product.variants.length > 0 ? this.product.variants[0].id : null,
      }
    },
    computed: {
      productVariants() {
        let categories = []
        
        this.product.variants.map(variant => {
          if (variant.category) {
            let category = _.find(categories, {id: variant.category})
            if (!category) {
              category = {
                isDisabled: true,
                id: variant.category,
                label: variant.category,
                children: [],
              }
              categories.push(category)
            }
            category.children.push({
              id: variant.id,
              label: variant.name,
            })
          } else {
            categories.push({
              id: variant.id,
              label: variant.name,
            })
          }
        })

        return categories
      },
      outOfStock() {
        return this.product.quantity === 0 && (!this.product.variants || !this.product.variants.length)
      },
      invoice_item() {
        if (!this.invoice ||
          !this.invoice.invoice_items ||
          this.invoice.invoice_items.length == 0)
          return null
        return _.find(this.invoice.invoice_items, {[`${this.productType}_id`]: this.product.id})
      },
      canApplyCoupon() {
        return !this.product.purchase_generates_gift_card &&
          !this.product.promo_code &&
          this.product.accept_coupon
      },
      allowSettingAmount() {
        return this.product.purchase_generates_gift_card &&
          !!this.product.gift_card_rules &&
          this.product.gift_card_rules.allow_setting_amount &&
          !this.product.promo_code
      },
      priceError() {
        if (!this.allowSettingAmount) return null
        let minError = `${trans('minimum')} ${_.currency(this.product.gift_card_rules.minimum)}`
        let maxError = `${trans('maximum')} ${_.currency(this.product.gift_card_rules.maximum)}`
        if (!this.price || !_.isNumeric(this.price)) return minError
        let price = parseFloat(this.price)
        if (price < 5 ||
          this.product.gift_card_rules.minimum > 0 && price < this.product.gift_card_rules.minimum)
          return minError
        if (price > 1000 ||
          this.product.gift_card_rules.maximum > 0 && price > this.product.gift_card_rules.maximum)
          return maxError
        return null
      },
    },
    methods: {
      setTopAmount(price) {
        let el = document.querySelector(`#product-card-${this.product.id} h2.price`)
        el.innerHTML = _.currency(price ? price : this.price)
      },
      setImage(image) {
        let el = document.querySelector(`#product-card-${this.product.id} .card-img-top`)
        el.src = image
        let carouselEl = document.querySelector(`#product-card-${this.product.id} .carousel-indicators`)
        if (carouselEl) {
          carouselEl.style.display = 'none';
        }
      },
      onSetAmount() {
        if (!!this.priceError) return
        this.show_set_amount = false
        let el = document.querySelector(`#product-card-${this.product.id} h2.price`)
        el.innerHTML = this.price
      },
      onCloseApplyCoupon() {
        this.show_apply_coupon = false
      },
      onCouponApplied(invoice) {
        this.$emit('invoice', invoice)
        this.onCloseApplyCoupon()
      },
      onAdminOptionsApplied(invoice) {
        this.$emit('invoice', invoice)
      },
      removeCoupon(invoice_item) {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/${this.productType}/${this.invoice_item.id}/coupon`
        axios.delete(url)
        .then(response => {
          this.busy = false
          this.invoice = response.data
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onAddToCart() {
        this.busy = true
        this.error = null
        axios.post(`/org/${this.organization.id}/cart/${this.productType}`, {
          [`${this.productType}_id`]: this.product.id,
          quantity: this.quantity,
          price: this.price,
          variant_id: this.variant_id,
        })
        .then(response => {
          this.busy = false
          this.invoice = response.data
          this.$emit('invoice', response.data)

          let variant = null
          if (this.variant_id) {
            variant = _.find(this.product.variants, {id: this.variant_id})
          }
          
          if (variant) {
            this.$bvToast.toast(`${variant.name} x ${this.quantity}`, {
              title: this.trans('added_to_cart_success'),
              autoHideDelay: 5000,
              variant: 'success',
              toaster: 'b-toaster-bottom-left',
            })
          }
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onRemoveFromCart() {
        this.busy = true
        this.error = null
        axios.delete(`/org/${this.organization.id}/cart/${this.productType}/${this.invoice_item.id}`)
        .then(response => {
          this.busy = false
          this.invoice = response.data
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
    },
    watch: {
      variant_id() {
        let variant = _.find(this.product.variants, {id: this.variant_id})
        this.price = variant.price
        this.product.quantity = variant.quantity
        if (variant.image_url) {
          this.setImage(variant.image_url)
        } else {
          this.setImage(this.product.image_url)
        }
        
        this.setTopAmount()
      },
    },
    mounted() {
      window['product_' + this.product.id] = this
    },
  }
</script>
